import {FILTER_PRIORITY_DISPLAY_THRESHOLD} from '../constants/filter-constants'
import {HasFilterProvider} from '../providers/has'
import {NoFilterProviderV2} from '../providers/no'
import {type FilterProvider, type FilterSuggestion, FilterValueType, type SuppliedFilterProvider} from '../types'
import {isNestedFilterProvider} from './index'

/**
 * Given a list of caller-supplied filter providers (possibly including nested groups), returns a
 * keyed record of all individual providers plus synthesized `no` and `has` aggregate providers
 * when any of the inputs declare `valueless` or `hasValue` filter types respectively.
 *
 * Pass `Object.values(record)` to the `FilterQueryParser` constructor to get a parser that knows
 * how to handle `no:` / `has:` queries.
 *
 * Callers passing a `customParser` to `FilterQueryContextProvider` should use this helper to
 * build the provider list they hand to their parser's constructor — otherwise their parser will
 * not recognize the synthesized aggregates.
 */
export const synthesizeAggregateProviders = (providers: SuppliedFilterProvider[]): Record<string, FilterProvider> => {
  const record: Record<string, FilterProvider> = {}
  const noProviders: FilterProvider[] = []
  const hasProviders: FilterProvider[] = []

  for (const provider of providers) {
    if (isNestedFilterProvider(provider)) {
      for (const subProvider of provider.filterProviders) {
        record[subProvider.key] = subProvider
        if (subProvider.options.filterTypes.valueless) noProviders.push(subProvider)
        if (subProvider.options.filterTypes.hasValue) hasProviders.push(subProvider)
      }
    } else {
      record[provider.key] = provider
      if (provider.options.filterTypes.valueless) noProviders.push(provider)
      if (provider.options.filterTypes.hasValue) hasProviders.push(provider)
    }
  }

  if (noProviders.length) {
    record.no = new NoFilterProviderV2(toPresenceSuggestions(noProviders))
  }

  if (hasProviders.length) {
    record.has = new HasFilterProvider(toPresenceSuggestions(hasProviders))
  }

  return record
}

const toPresenceSuggestions = (providers: FilterProvider[]): FilterSuggestion[] =>
  providers
    .sort((a, b) => (a.displayName ?? a.key)?.localeCompare(b.displayName ?? b.key) ?? 0)
    .map(provider => ({
      value: provider.key,
      priority: FILTER_PRIORITY_DISPLAY_THRESHOLD,
      displayName: provider.displayName,
      type: FilterValueType.Value,
      icon: provider.icon,
    }))
